import { useCallback, useEffect, useRef, useState } from 'react'

import type { AssistantImageQuality, CreativeFormatId } from '@digital-twins/shared-types'
import type { AssistantImageRequest } from '@digital-twins/shared-types/assistant'
import type { ImageApprovalBatchState, ImageApprovalPair, ImageApprovalStep } from '@/types/components/thread-image-approval'

export interface UseImageApprovalBatchesProps {
  threadToken?: string
  /**
   * One paid approval. `true` only when its image was created; `failed` when its turn ran and
   * failed; `master_failed` when admission refused a format because its image's master failed;
   * `conflict` when another tab fixed the card first, so the run stops on what the server saved;
   * `unstarted` when it never reached the provider, so it charged and locked nothing;
   * `context_unavailable` when a brand or reference on it can no longer be used, also unstarted.
   */
  approve: (
    batchKey: string,
    request: AssistantImageRequest,
    quality: AssistantImageQuality,
    /** The thread the batch started on; a different one must refuse. */
    threadToken: string | undefined,
    /** The format this approval admits and the card's whole selection; absent without a selection. */
    format?: { formatId: CreativeFormatId; formats: Array<CreativeFormatId> },
    /** Which image of the card this is, on a card with more than one. The server enforces it. */
    step?: ImageApprovalStep,
    /** On a try, its number. On the rest, the tries the card showed. */
    tries?: number,
  ) => Promise<boolean | 'failed' | 'master_failed' | 'conflict' | 'unstarted' | 'context_unavailable' | undefined>
}

/**
 * Owns the sequential "approve every image" loop at page level.
 *
 * The approval card cannot own it: the first approval appends a new question,
 * which moves the card's turn out of the current-exchange container, so React
 * remounts the card and a loop living in it stops after the first image.
 * The loop stops when the thread changes or the page unmounts, and never
 * retries a paid approval on its own. When a set's master fails, that image's other
 * formats are skipped and the other images carry on; anything else pauses the card.
 */
export function useImageApprovalBatches(props: UseImageApprovalBatchesProps) {
  const { threadToken, approve } = props

  const [batches, setBatches] = useState<Record<string, ImageApprovalBatchState>>({})
  const approveRef = useRef(approve)
  approveRef.current = approve
  const threadRef = useRef(threadToken)
  threadRef.current = threadToken
  const runningRef = useRef(new Set<string>())
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    setBatches({})
    runningRef.current = new Set()
  }, [threadToken])

  const patch = useCallback((batchKey: string, next: Partial<ImageApprovalBatchState>) => {
    setBatches((prev) => {
      const current: ImageApprovalBatchState = prev[batchKey] ?? { quality: 'medium', progress: null, paused: false }
      return { ...prev, [batchKey]: { ...current, ...next } }
    })
  }, [])

  const generate = useCallback(
    async (
      batchKey: string,
      pairs: Array<Pick<ImageApprovalPair, 'request' | 'formatId'>>,
      quality: AssistantImageQuality,
      formats?: Array<CreativeFormatId> | null,
      step?: ImageApprovalStep,
      tries?: number,
    ) => {
      const startedOn = threadRef.current
      const running = runningRef.current
      if (running.has(batchKey) || pairs.length === 0) {
        return
      }
      running.add(batchKey)
      const isAlive = () => mountedRef.current && threadRef.current === startedOn
      patch(batchKey, { quality, paused: false, contextRefused: undefined, pickedFormats: undefined, ...(formats ? { formats } : {}) })
      let paused = false
      let isUnstarted = false
      let contextRefused = false
      const failedMasters = new Set<string>()
      try {
        for (const [index, { request, formatId }] of pairs.entries()) {
          if (!isAlive()) {
            return
          }
          if (failedMasters.has(request.requestId)) {
            continue
          }
          patch(batchKey, { progress: { current: index + 1, total: pairs.length } })
          const completed = await approveRef.current(
            batchKey,
            request,
            quality,
            startedOn,
            formatId && formats ? { formatId, formats } : undefined,
            step,
            tries,
          )
          if (completed === 'conflict') {
            break
          }
          // The first image failing is not a pause: the card offers its try and the next concept.
          if (completed === 'failed' && (step === 'first' || step === 'try') && !(formats && formats.length > 1)) {
            break
          }
          const isFailedMaster = completed === 'failed' && formats && formats.length > 1 && formatId === formats[0]
          if (completed === 'master_failed' || isFailedMaster) {
            failedMasters.add(request.requestId)
            continue
          }
          if (completed !== true) {
            paused = true
            contextRefused = completed === 'context_unavailable'
            isUnstarted = completed === 'unstarted' || contextRefused
            break
          }
        }
      } catch {
        // The turn owns its error. Never automatically retry a paid approval.
        paused = true
      } finally {
        running.delete(batchKey)
        if (isAlive()) {
          patch(batchKey, { progress: null, paused, contextRefused: contextRefused || undefined, ...(isUnstarted ? { formats: undefined, pickedFormats: formats ?? undefined } : {}) })
        }
      }
    },
    [patch],
  )

  return { batches, generate }
}
